import React, { useEffect, useState } from "react";
// import "./pass_generator.css";
import { Box } from "./passStyle";
import { ToolsHeader } from "../components/ToolsHeader";
import { isInIframe } from "../../utils/is-in-iframe";

type Strength = {
  text: "Weak" | "Good" | "Strong";
  color: string;
};

const calculatePasswordStrength = (password: string): Strength => {
  let score = 0;
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecial = /[!@#$%^&*()_+\-=[\]{};:,.<>?]/.test(password);

  if (password.length < 8) {
    return { text: "Weak", color: "#d9534f" };
  } else if (password.length >= 12) {
    score += 2;
  } else if (password.length >= 8) {
    score += 1;
  }

  if (hasUppercase) score += 1;
  if (hasLowercase) score += 1;
  if (hasNumbers) score += 1;
  if (hasSpecial) score += 1;

  if (score >= 5) {
    return { text: "Strong", color: "#8CC540" };
  } else if (score >= 3) {
    return { text: "Good", color: "#2c9f45" };
  } else {
    return { text: "Weak", color: "#d9534f" };
  }
};

const PasswordGenerator: React.FC = () => {
  const [password, setPassword] = useState("");
  const [length, setLength] = useState(12);
  const [includeDigits, setIncludeDigits] = useState(true);
  const [includeSpecial, setIncludeSpecial] = useState(true);
  const [includeUpper, setIncludeUpper] = useState(true);
  const [includeLower, setIncludeLower] = useState(true);
  const [easyRemember, setEasyRemember] = useState(false);
  const [strength, setStrength] = useState<Strength>({
    text: "Strong",
    color: "#8CC540",
  });
  const [history, setHistory] = useState<string[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  }, [history, showHistory]);
  useEffect(() => {
    generatePassword();
    const savedHistory = localStorage.getItem("passwordHistory");
    if (savedHistory) setHistory(JSON.parse(savedHistory));
  }, [
    length,
    includeUpper,
    includeLower,
    includeDigits,
    includeSpecial,
    easyRemember,
  ]);
  useEffect(() => {
    generatePassword();
    const savedHistory = localStorage.getItem("passwordHistory");
    if (savedHistory) setHistory(JSON.parse(savedHistory));
  }, []);

  useEffect(() => {
    setStrength(calculatePasswordStrength(password));
  }, [password]);

  const generatePassword = () => {
    // ensure length is a number
    let pwdLength = length;

    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const digits = "0123456789";
    const special = "!@#$%^&*()_+-=[]{}|;:,.<>?";
    const vowels = "aeiou";
    const consonants = "bcdfghjklmnpqrstvwxyz";

    const randFrom = (s: any) => s[Math.floor(Math.random() * s.length)];

    const shuffleArray = (arr: any) => {
      // Fisher–Yates
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
    };

    // at least one option must be chosen or easyRemember must be true
    if (
      !includeUpper &&
      !includeLower &&
      !includeDigits &&
      !includeSpecial &&
      !easyRemember
    ) {
      alert("Please select at least one character type");
      return;
    }

    let pwd = "";

    if (!easyRemember) {
      // Normal random pool flow (guarantee one of each selected type)
      let pool = "";
      if (includeUpper) pool += upper;
      if (includeLower) pool += lower;
      if (includeDigits) pool += digits;
      if (includeSpecial) pool += special;

      const guarantees = [];
      if (includeUpper) guarantees.push(randFrom(upper));
      if (includeLower) guarantees.push(randFrom(lower));
      if (includeDigits) guarantees.push(randFrom(digits));
      if (includeSpecial) guarantees.push(randFrom(special));

      // If requested length is smaller than number of guarantees, bump it (or alert instead)
      if (pwdLength < guarantees.length) {
        // you can change this to `alert(...)` and return if you don't want automatic bumping
        pwdLength = guarantees.length;
        console.warn(
          `Length increased to ${pwdLength} to satisfy chosen options.`
        );
      }

      const result = [];

      // Fill remaining with random chars from pool
      const remaining = pwdLength - guarantees.length;
      for (let i = 0; i < remaining; i++) {
        result.push(randFrom(pool));
      }

      // add guaranteed chars then shuffle
      result.push(...guarantees);
      shuffleArray(result);
      pwd = result.join("");
    } else {
      // easyRemember: create alternating consonant/vowel base of exact length
      let base = "";
      for (let i = 0; i < pwdLength; i++) {
        base += i % 2 === 0 ? randFrom(consonants) : randFrom(vowels);
      }

      // If user requested only uppercase and NOT lowercase, uppercase whole base.
      if (includeUpper && !includeLower) {
        base = base.toUpperCase();
      }

      // We need distinct positions for replacements (digit/special) and a distinct position for forcing uppercase
      // Count unique modifications that require distinct slots:
      let requiredDistinct = 0;
      if (includeUpper && includeLower) requiredDistinct += 1; // need a letter to uppercase while leaving some lowercases
      if (includeDigits) requiredDistinct += 1;
      if (includeSpecial) requiredDistinct += 1;

      if (pwdLength < requiredDistinct) {
        // bump length (or alert + return)
        pwdLength = requiredDistinct;
        // rebuild base with new length
        base = "";
        for (let i = 0; i < pwdLength; i++) {
          base += i % 2 === 0 ? randFrom(consonants) : randFrom(vowels);
        }
        console.warn(
          `Length increased to ${pwdLength} to satisfy Easy+options.`
        );
      }

      // create shuffled indices to pick distinct positions
      const indices = Array.from({ length: pwdLength }, (_, i) => i);
      shuffleArray(indices);
      let idxPointer = 0;
      const used = new Set();

      // If both upper+lower are requested, reserve one index to uppercase later (but keep it a letter)
      if (includeUpper && includeLower) {
        // find a letter index (all are letters here)
        const pos = indices[idxPointer++];
        base =
          base.slice(0, pos) + base[pos].toUpperCase() + base.slice(pos + 1);
        used.add(pos);
      } else if (includeUpper && !includeLower) {
        // already uppercased whole base above
      }

      // replace a distinct position with a digit if requested
      if (includeDigits) {
        // find next unused index
        while (idxPointer < indices.length && used.has(indices[idxPointer]))
          idxPointer++;
        if (idxPointer < indices.length) {
          const pos = indices[idxPointer++];
          base = base.slice(0, pos) + randFrom(digits) + base.slice(pos + 1);
          used.add(pos);
        }
      }

      // replace a distinct position with a special char if requested
      if (includeSpecial) {
        while (idxPointer < indices.length && used.has(indices[idxPointer]))
          idxPointer++;
        if (idxPointer < indices.length) {
          const pos = indices[idxPointer++];
          base = base.slice(0, pos) + randFrom(special) + base.slice(pos + 1);
          used.add(pos);
        }
      }

      // Ensure there's at least one uppercase if includeUpper && includeLower (in case previous replacements removed it)
      if (includeUpper && includeLower && !/[A-Z]/.test(base)) {
        // uppercase any letter (not digit/special)
        const letterPositions = [...Array(pwdLength).keys()].filter((i) =>
          /[a-zA-Z]/.test(base[i])
        );
        if (letterPositions.length > 0) {
          const pos = randFrom(letterPositions);
          base =
            base.slice(0, pos) + base[pos].toUpperCase() + base.slice(pos + 1);
        }
      }

      pwd = base;
    }

    // final sanity check: ensure exact length
    if (pwd.length !== pwdLength) {
      // fallback: trim or pad (shouldn't be necessary with above logic)
      pwd = pwd.slice(0, pwdLength);
    }

    setPassword(pwd);
    saveToHistory(pwd);
  };

  const saveToHistory = (pwd: string) => {
    const newHistory = [pwd, ...history.slice(0, 4)];
    setHistory(newHistory);
    localStorage.setItem("passwordHistory", JSON.stringify(newHistory));
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(password);
      alert("Password copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy password:", err);
    }
  };
  return (
    <Box>
      <div
        className={`password-generator ${
          isInIframe ? "interDisplay" : "defaultText"
        }`}
      >
        {!isInIframe && (
          <ToolsHeader
            title="Instantly generate a secure, random password with the Coozmoo Password
          Generator online tool"
            description="Use our online password generator tool to instantly create a secure,
          random password."
          />
        )}
        {/* <h1>
          Instantly generate a secure, random password with the Coozmoo Password
          Generator online tool{" "}
        </h1>
        <p>
          Use our online password generator tool to instantly create a secure,
          random password.
        </p> */}

        <div className="controls">
          <input
            type="text"
            value={password}
            readOnly
            style={{ borderColor: strength.color }}
          />
          <div className="bottonsbyus">
            <button onClick={generatePassword} title="Generate New Password">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                // width="24"
                // height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                className="heWi"
              >
                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                <path d="M21 3v5h-5" />
                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                <path d="M3 21v-5h5" />
              </svg>
            </button>
            <button onClick={copyToClipboard} title="Copy Password">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                // width="24"
                // height="24"
                className="heWi"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
              </svg>
            </button>
          </div>
        </div>

        <div className="password-status" style={{ color: strength.color }}>
          {strength.text}
        </div>

        <div className="customize-your-password">
          <h3>Customize your password</h3>

          <div className="checkbox-slider">
            <div className="slider-container">
              <h5>Password Length:</h5>
              <div className="passwordlabel">
                <span id="passwordLengthCount">{length}</span>
                <input
                  type="range"
                  id="passwordLength"
                  min={4}
                  max={32}
                  value={length}
                  onChange={(e) => {
                    setLength(parseInt(e.target.value));
                  }}
                />
              </div>
            </div>
            <div className="checkbox-group">
              <label htmlFor="easyRemember" className="toggle-switch">
                <input
                  type="checkbox"
                  id="easyRemember"
                  checked={easyRemember}
                  onChange={(e) => {
                    setEasyRemember(e.target.checked);
                  }}
                />{" "}
                <span className="slider"></span>
                <label htmlFor="easyRemember">Easy to Remember?</label>
              </label>

              <label htmlFor="digit" className="toggle-switch">
                <input
                  type="checkbox"
                  id="digit"
                  checked={includeDigits}
                  onChange={(e) => {
                    setIncludeDigits(e.target.checked);
                  }}
                />
                <span className="slider"></span>
                <label htmlFor="digit">Include Digits?</label>
              </label>
              <label htmlFor="specialChar" className="toggle-switch">
                <input
                  type="checkbox"
                  id="specialChar"
                  checked={includeSpecial}
                  onChange={(e) => {
                    setIncludeSpecial(e.target.checked);
                  }}
                />
                <span className="slider"></span>
                <label htmlFor="specialChar">Include Special Characters?</label>
              </label>
              <label htmlFor="uppercase" className="toggle-switch">
                <input
                  type="checkbox"
                  id="uppercase"
                  checked={includeUpper}
                  onChange={(e) => {
                    setIncludeUpper(e.target.checked);
                  }}
                />
                <span className="slider"></span>
                <label htmlFor="uppercase">Include Uppercase?</label>
              </label>
              <label htmlFor="lowercase" className="toggle-switch">
                <input
                  type="checkbox"
                  id="lowercase"
                  checked={includeLower}
                  onChange={(e) => {
                    setIncludeLower(e.target.checked);
                  }}
                />
                <span className="slider"></span>
                <label htmlFor="lowercase">Include Lowercase?</label>
              </label>
            </div>
          </div>
        </div>

        <button
          onClick={() => {
            setShowHistory(!showHistory);
            const height = document.body.scrollHeight;
            window.parent.postMessage({ type: "setHeight", height }, "*");
          }}
          id="toggleHistory"
        >
          {showHistory ? "Hide Password History" : "Show Password History"}
        </button>

        {showHistory && (
          <div className="password-history" id="passwordHistory">
            <h4>Password History</h4>
            <div>
              {history.map((pwd, idx) => (
                <div key={idx}>{pwd}</div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Box>
  );
};

export default PasswordGenerator;
